/*
 * Test Candidates sign-up source (tc-signup-source).
 *
 * Remembers how a visitor first reached www.testcandidates.com, so the sign-up
 * can say where the new organisation came from. Our own record, not Google's:
 * it is only sent to our API with a sign-up (admin SignupSource decides the
 * channel) and never shared.
 *
 * Why it may run without consent (approved 8 Oct 2026, roadmap TC fast track):
 * - UK: the PECR statistics exemption (Data (Use and Access) Act 2025,
 *   Schedule A1, in force 5 Feb 2026). First party, no identifier, sole purpose
 *   is seeing which pages lead to new accounts. Disclosed in the cookie table.
 * - Objection: nothing is stored when the browser sends Global Privacy
 *   Control or Do Not Track.
 * - EEA, Iceland, Liechtenstein, Norway, Switzerland: no exemption, so only
 *   after analytics consent. The browser cannot see the country, so a
 *   European time zone other than the UK's counts as one of these regions.
 * - Ad click ids (gclid, msclkid) are kept as a yes/no flag; the id itself
 *   only with advertising consent. At sign-up the Google click id may also come
 *   from Google's own _gcl_aw cookie (see adClick).
 * - The ChatGPT ads click key (oppref, OpenAI's opaque attribution id) is kept
 *   only while advertising is allowed (see adConsent), otherwise as a yes/no
 *   flag. The admin sends a verified sign-up from a ChatGPT ad to OpenAI Ads
 *   with it (ChatGPTAdsConversions).
 * - The first visit decides the channel, but the most recent ad click is kept
 *   alongside it (lastClick, in the same record, same consent rules), so a visitor who
 *   first came another way and later clicks an ad still reaches the ad
 *   platform's conversion (lastClick).
 *
 * Partner code (TC partner API, 19 Oct 2026): a link from a partner's product
 * carries `?partner=<code>`. The code names the partner, not the person, and
 * is needed to honour the partner agreement (their commission), so it is kept
 * on its own key for 30 days whatever the consent choice, and sent with the
 * sign-up as `partnerCode`. Nothing else about the visit is stored with it.
 *
 * Canonical copy: picked/resources/signup-source/. The splash copies it in at
 * build; the employer app imports it. ES5 only, it is served untranspiled.
 */
;(function (root) {
  var KEY = 'tc-signup-source'
  var PARTNER_KEY = 'tc-partner'
  var DAYS = 30
  var UTM = ['source', 'medium', 'campaign', 'term', 'content']
  var UK_ZONES = ['Europe/London', 'Europe/Belfast', 'Europe/Guernsey', 'Europe/Jersey', 'Europe/Isle_of_Man', 'GB']
  var EEA_ATLANTIC = ['Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Faroe', 'Arctic/Longyearbyen']
  var AUTH_HOSTS = ['accounts.google.com', 'login.microsoftonline.com', 'login.live.com', 'checkout.stripe.com', 'billing.stripe.com']

  function store () {
    try { return root.localStorage } catch (e) { return null }
  }

  function consent () {
    var s = store()
    var r = null
    try { r = JSON.parse(s && s.getItem('picked-consent')) } catch (e) {}
    var ts = r ? Date.parse(r.ts) : NaN
    var now = new Date().getTime()
    var usable = !!(r && r.v === 1 && !isNaN(ts) && now - ts <= 365 * 864e5 && ts - now <= 864e5)
    return { analytics: usable && r.analytics === true, advertising: usable && r.advertising === true, advertisingRefused: usable && r.advertising === false }
  }

  function objected () {
    var n = root.navigator || {}
    return n.globalPrivacyControl === true || n.doNotTrack === '1' || n.doNotTrack === 'yes' || root.doNotTrack === '1'
  }

  function needsConsent () {
    var zone = ''
    try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) {}
    if (UK_ZONES.indexOf(zone) !== -1) return false
    return zone.indexOf('Europe/') === 0 || EEA_ATLANTIC.indexOf(zone) !== -1
  }

  function param (query, name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(query)
    if (!m) return ''
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')) } catch (e) { return '' }
  }

  function referrerHost (doc) {
    var m = /^https?:\/\/([^\/?#:]+)/i.exec(doc.referrer || '')
    if (!m) return ''
    var host = m[1].toLowerCase()
    if (/(^|\.)testcandidates\.com$/.test(host) || host === root.location.hostname) return ''
    if (AUTH_HOSTS.indexOf(host) !== -1) return ''
    return host
  }

  /* The partner code from this page's URL (lower case, 3 to 40 of a-z, 0-9 and hyphen), or ''. */
  function partnerParam (query) {
    var v = (param(query, 'partner') || '').toLowerCase()
    return /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(v) ? v : ''
  }

  function capturePartner () {
    if (!/(^|\.)testcandidates\.com$/.test(root.location.hostname)) return null
    var s = store()
    if (!s) return null
    var code = partnerParam(root.location.search || '')
    if (code) {
      try { s.setItem(PARTNER_KEY, JSON.stringify({ v: 1, code: code, at: new Date().toISOString() })) } catch (e) {}
      return code
    }
    return readPartner()
  }

  function readPartner () {
    var s = store()
    var r = null
    try { r = JSON.parse(s && s.getItem(PARTNER_KEY)) } catch (e) {}
    if (!r || r.v !== 1 || isNaN(Date.parse(r.at)) || new Date().getTime() - Date.parse(r.at) > DAYS * 864e5) return null
    return /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(r.code || '') ? r.code : null
  }

  function read () {
    var s = store()
    var r = null
    try { r = JSON.parse(s && s.getItem(KEY)) } catch (e) {}
    if (!r || r.v !== 1 || isNaN(Date.parse(r.at))) return null
    if (new Date().getTime() - Date.parse(r.at) > DAYS * 864e5) return null
    return r
  }

  function hasSource (r) {
    return !!(r && (r.referrer || r.gclid || r.msclkid || r.oppref || (r.utm && r.utm.source)))
  }

  /* First visit wins, except that a visit from an ad or a tagged link replaces
     a record that only says "came direct". */
  function capture () {
    if (!/(^|\.)testcandidates\.com$/.test(root.location.hostname)) return null
    if (objected()) return null
    var c = consent()
    if (needsConsent() && !c.analytics) return null
    var s = store()
    if (!s) return null
    var q = root.location.search || ''
    var r = { v: 1, at: new Date().toISOString(), landing: (root.location.pathname || '/').slice(0, 300), referrer: referrerHost(root.document), utm: {} }
    for (var i = 0; i < UTM.length; i++) {
      var v = param(q, 'utm_' + UTM[i])
      if (v) r.utm[UTM[i]] = v.slice(0, 150)
    }
    var ids = ['gclid', 'msclkid']
    for (var j = 0; j < ids.length; j++) {
      var id = param(q, ids[j])
      if (id) r[ids[j]] = c.advertising ? id.slice(0, 255) : true
    }
    var op = opprefParam(q)
    if (op) r.oppref = adConsent() !== 'denied' ? op : true
    /* Google's campaign id on an auto-tagged ad click: names the campaign,
       not the person, so it is kept whatever the consent. */
    var gad = param(q, 'gad_campaignid') || param(q, 'tc_cid')
    if (/^\d{1,20}$/.test(gad)) r.gadCampaignId = gad
    /* The latest ad click, whatever the first visit was. Runs again on
       pagehide, so an Accept on the landing page keeps that click's ids. */
    var click = clickIds(q)
    var existing = read()
    var keep = existing && (hasSource(existing) || !hasSource(r)) ? existing : r
    if (click) keep.lastClick = click
    else if (keep === r && existing && existing.lastClick) keep.lastClick = existing.lastClick
    if (keep === existing && !click) return existing
    try { s.setItem(KEY, JSON.stringify(keep)) } catch (e) { return null }
    return keep
  }

  /* What the sign-up sends: the stored record, or failing that what this page
     itself can see (nothing is stored for that). */
  function forSignup () {
    var r = read()
    if (!r && /(^|\.)testcandidates\.com$/.test(root.location.hostname) && !objected()) {
      r = { v: 1, at: new Date().toISOString(), landing: (root.location.pathname || '/').slice(0, 300), referrer: referrerHost(root.document), utm: {} }
      for (var i = 0; i < UTM.length; i++) {
        var v = param(root.location.search || '', 'utm_' + UTM[i])
        if (v) r.utm[UTM[i]] = v.slice(0, 150)
      }
      if (param(root.location.search || '', 'gclid')) r.gclid = true
      if (param(root.location.search || '', 'msclkid')) r.msclkid = true
      var op = opprefParam(root.location.search || '')
      if (op) r.oppref = op
      var gad = param(root.location.search || '', 'gad_campaignid') || param(root.location.search || '', 'tc_cid')
      if (/^\d{1,20}$/.test(gad)) r.gadCampaignId = gad
    }
    var g = ga()
    var partner = readPartner() || partnerParam(root.location.search || '')
    if (!r) { var only = lastClick(); var bare = only ? { ga: g, lastClick: only } : { ga: g }; if (partner) bare.partnerCode = partner; return bare }
    var gclid = typeof r.gclid === 'string' ? r.gclid : (adClick() || r.gclid || false)
    /* The click key goes only while advertising is still allowed at sign-up. */
    var ads = adConsent()
    var oppref = typeof r.oppref === 'string' && ads !== 'denied' ? r.oppref : !!r.oppref
    var out = { at: r.at, landing: r.landing, referrer: r.referrer || '', utm: r.utm || {}, gclid: gclid, msclkid: r.msclkid || false, oppref: oppref, ads: ads, ga: g }
    if (r.gadCampaignId) out.gadCampaignId = r.gadCampaignId
    var last = lastClick()
    if (last) out.lastClick = last
    if (partner) out.partnerCode = partner
    return out
  }

  /* The click ids an ad landing URL carries that consent lets us keep, or null. */
  function clickIds (query) {
    var c = consent()
    var out = { v: 1, at: new Date().toISOString() }
    var any = false
    var ids = ['gclid', 'msclkid']
    for (var i = 0; i < ids.length; i++) {
      var id = param(query, ids[i])
      if (id && c.advertising && /^[A-Za-z0-9_-]{8,255}$/.test(id)) { out[ids[i]] = id; any = true }
    }
    var op = opprefParam(query)
    if (op && adConsent() !== 'denied') { out.oppref = op; any = true }
    return any ? out : null
  }

  /* The most recent ad click for the sign-up: the stored one, or this page's
     own; ids are dropped if advertising was refused since. */
  function lastClick () {
    var stored = read()
    var r = stored && stored.lastClick && !isNaN(Date.parse(stored.lastClick.at)) ? stored.lastClick : null
    if (!r && /(^|\.)testcandidates\.com$/.test(root.location.hostname)) r = clickIds(root.location.search || '')
    if (!r || objected()) return null
    var out = { at: r.at }
    var c = consent()
    if (!c.advertisingRefused) {
      if (r.gclid) out.gclid = r.gclid
      if (r.msclkid) out.msclkid = r.msclkid
    }
    if (r.oppref && adConsent() !== 'denied') out.oppref = r.oppref
    return (out.gclid || out.msclkid || out.oppref) ? out : null
  }

  /* OpenAI's oppref is opaque: kept unchanged, printable ASCII only. */
  function opprefParam (query) {
    var v = param(query, 'oppref')
    return /^[\x21-\x7E]{1,512}$/.test(v) ? v : ''
  }

  /* Whether advertising measurement is allowed, as Google Consent Mode reads
     it on this site: 'granted' (accepted), 'default' (no choice outside the
     UK, EEA and Switzerland, where advertising starts granted), or 'denied'
     (rejected, objected, or no choice in the UK, EEA or Switzerland). */
  function adConsent () {
    if (objected()) return 'denied'
    var c = consent()
    if (c.advertising) return 'granted'
    if (c.advertisingRefused) return 'denied'
    var stored = null
    try { stored = JSON.parse(store().getItem('picked-consent')) } catch (e) {}
    if (stored && stored.advertising === false) return 'denied'
    return (needsConsent() || ukZone()) ? 'denied' : 'default'
  }

  /* What GA4 may hear about this sign-up (admin TCJourney sends the journey
     events server-side). 'granted': analytics accepted on the banner.
     'default': no choice stored, outside the UK, EEA and Switzerland, where
     analytics starts granted (picked #5622). 'denied': rejected, objected
     (GPC or DNT), or no choice in the UK, EEA or Switzerland; then no ids are
     sent and the admin sends nothing to Google for this organisation. The
     client id comes from GA's own _ga cookie, which GA writes only while
     analytics storage is granted. */
  function ga () {
    var c = consent()
    var stored = null
    try { stored = JSON.parse(store().getItem('picked-consent')) } catch (e) {}
    var state
    if (objected() || (stored && stored.analytics === false)) state = 'denied'
    else if (c.analytics) state = 'granted'
    else state = (needsConsent() || ukZone()) ? 'denied' : 'default'
    if (state === 'denied') return { consent: state }
    var out = { consent: state }
    var cookies = root.document.cookie || ''
    var m = /(?:^|;\s*)_ga=GA\d\.\d\.(\d{1,12}\.\d{1,12})(?:;|$)/.exec(cookies)
    if (m) out.clientId = m[1]
    /* _ga_<stream>=GS1.1.<session>.… or GS2.1.s<session>$o… */
    var s = /(?:^|;\s*)_ga_[A-Z0-9]+=GS\d\.\d\.s?(\d{1,12})/.exec(cookies)
    if (s) out.sessionId = s[1]
    return out
  }

  function ukZone () {
    var zone = ''
    try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) {}
    return UK_ZONES.indexOf(zone) !== -1
  }

  /* The Google Ads click id from Google's own conversion linker cookie
     (_gcl_aw = GCL.<time>.<gclid>). Google writes it only while ad storage is
     granted, so it follows the Consent Mode region defaults; we skip it when
     the visitor objected or chose no advertising. The admin turns verified
     sign-ups and first payments with this id into Google Ads conversions. */
  function adClick () {
    if (objected() || consent().advertisingRefused) return ''
    var m = /(?:^|;\s*)_gcl_aw=([^;]+)/.exec(root.document.cookie || '')
    var parts = m ? m[1].split('.') : []
    var id = parts.length >= 3 ? parts.slice(2).join('.') : ''
    return /^[A-Za-z0-9_-]{8,255}$/.test(id) ? id : ''
  }

  var api = { capture: capture, read: read, forSignup: forSignup, KEY: KEY }
  root.TCSignupSource = api
  try { capture() } catch (e) {}
  try { capturePartner() } catch (e) {}
  /* A visitor who accepts the banner on the landing page keeps the click key. */
  try { root.addEventListener('pagehide', function () { try { capture() } catch (e) {} }) } catch (e) {}
  if (typeof module !== 'undefined' && module.exports) module.exports = api
})(typeof window !== 'undefined' ? window : this)
